import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { Col, Row } from "antd/es/grid";
import { useIntl } from "react-intl";

import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import { EntityType } from "@mapmycustomers/shared/types/entity";

import ButtonLink from "@app/component/ButtonLink";
import NextActivityState from "@app/component/frequency/NextActivityState";
import FrequencyPreviewConfig from "@app/component/frequency/type/FrequencyPreviewConfig";
import {
  getFrequencyPanelConfig,
  getPendingActivity,
  isFrequencyPanelLoading,
} from "@app/store/frequency";
import { initFrequencyModal, initFrequencyPanel } from "@app/store/frequency/actions";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useToggleBooleanAfterTimeout from "@app/util/hook/useToggleBooleanAfterTimeout";

import useFrequencyContext from "../../util/useFrequencyContext";
import FrequencyInfo from "../FrequencyInfo";
import FrequencySelectorWithConfig from "../FrequencySelectorWithConfig";
import Loader from "../Loader";
import PendingActivityState from "../PendingActivityState";

import styles from "./FrequencyState.module.scss";

interface Props {
  config: FrequencyPreviewConfig;
  hideActions: boolean;
  initFrequencyModal: typeof initFrequencyModal.request;
  loading: boolean;
  onFrequencyChange: (cadence: Partial<Cadenced>) => void;
  onInitFrequencyPanel: typeof initFrequencyPanel.request;
  subscribe: typeof subscribe;
  unsubscribe: typeof unsubscribe;
}

const FrequencyState: React.FC<Props> = ({
  config,
  hideActions,
  initFrequencyModal,
  loading,
  onFrequencyChange,
  onInitFrequencyPanel,
  subscribe,
  unsubscribe,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  const showLoading = useToggleBooleanAfterTimeout(1000, false, loading, true);

  const { entityDoesHaveFrequencyAlready, onUpdate } = useFrequencyContext();

  const handleUpdate = useCallback(() => {
    onInitFrequencyPanel();
    onUpdate?.();
  }, [onInitFrequencyPanel, onUpdate]);

  useEffect(() => {
    const listener = {
      onAdd: handleUpdate,
      onDelete: handleUpdate,
      onUpdate: handleUpdate,
    };

    subscribe({ entityTypes: [EntityType.ACTIVITY], listener });

    return () => {
      unsubscribe({ entityTypes: [EntityType.ACTIVITY], listener });
    };
  }, [handleUpdate, subscribe, unsubscribe]);

  if (showLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.container}>
      <NextActivityState config={config} hideActions={hideActions} />
      <PendingActivityState />
      {!entityDoesHaveFrequencyAlready && (
        <FrequencySelectorWithConfig onChange={onFrequencyChange} />
      )}
      {entityDoesHaveFrequencyAlready && (
        <Row className={styles.bottom} justify="space-between">
          <Col />
          <Col>
            <ButtonLink
              className={styles.manageLink}
              onClick={() =>
                initFrequencyModal({ analyticsPath: analytics.keyPath, entity: config.entity })
              }
            >
              {intl.formatMessage({
                id: "frequency.cadenceSelector.state.manageFrequencies",
                defaultMessage: "Manage Frequencies",
                description: "frequency component - state - Manage Frequencies",
              })}
            </ButtonLink>
          </Col>
          <Col>
            <FrequencyInfo hideText />
          </Col>
        </Row>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  config: getFrequencyPanelConfig(state),
  hideActions: !!getPendingActivity(state),
  loading: isFrequencyPanelLoading(state),
});

const mapDispatchToProps = {
  initFrequencyModal: initFrequencyModal.request,
  onInitFrequencyPanel: initFrequencyPanel.request,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(FrequencyState);
